import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Download, FileText, Calendar, Filter, TrendingUp, Store, Users, CreditCard, DollarSign, AlertTriangle, CheckCircle, Clock } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area } from 'recharts';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { es } from 'date-fns/locale/es';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Color palette
const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];
const CREDIT_COLORS = { credito: '#f59e0b', contado: '#10b981' };

export default function Reports() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stores, setStores] = useState([]);
  const [providers, setProviders] = useState([]);
  const [payments, setPayments] = useState([]);
  const [creditsAlert, setCreditsAlert] = useState([]);
  
  // Filters
  const [filterStore, setFilterStore] = useState('all');
  const [filterProvider, setFilterProvider] = useState('all');
  const [filterTipoPago, setFilterTipoPago] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Provider detail search
  const [providerSearch, setProviderSearch] = useState('');
  const [storeSearch, setStoreSearch] = useState('all');
  const [detailStatusFilter, setDetailStatusFilter] = useState('all');
  const [detailStartDate, setDetailStartDate] = useState('');
  const [detailEndDate, setDetailEndDate] = useState('');
  const [detailResults, setDetailResults] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailSearched, setDetailSearched] = useState(false);

  // Computed stats
  const [stats, setStats] = useState({
    totalPagado: 0,
    totalCredito: 0,
    totalContado: 0,
    totalPendiente: 0,
    countPagados: 0,
    countCredito: 0,
    countContado: 0,
    countPendientes: 0,
    byStore: [],
    byProvider: [],
    byMonth: [],
    byWeek: [],
    byMethod: [],
    byStoreMonthly: [],
    topStoreNames: [],
    creditosVencidos: 0,
    creditosPorVencer: 0
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    computeStats();
  }, [payments, filterStore, filterProvider, filterTipoPago, startDate, endDate]);

  const fetchData = async () => {
    try {
      const [paymentsRes, storesRes, providersRes, creditsRes] = await Promise.all([
        axios.get(`${API}/payments?date_filter=all`),
        axios.get(`${API}/stores`),
        axios.get(`${API}/providers`),
        axios.get(`${API}/reports/credits-alert`)
      ]);
      
      setPayments(paymentsRes.data || []);
      setStores(storesRes.data || []);
      setProviders(providersRes.data || []);
      setCreditsAlert(creditsRes.data || []);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const searchDetail = async () => {
    if (!providerSearch.trim() && storeSearch === 'all') {
      toast.error('Ingrese un proveedor o seleccione una sucursal para buscar');
      return;
    }
    setDetailLoading(true);
    setDetailSearched(true);
    try {
      const params = new URLSearchParams();
      if (providerSearch.trim()) params.append('provider_name', providerSearch.trim());
      if (storeSearch !== 'all') params.append('store_id', storeSearch);
      if (detailStatusFilter !== 'all') params.append('status', detailStatusFilter);
      if (detailStartDate) params.append('start_date', detailStartDate);
      if (detailEndDate) params.append('end_date', detailEndDate);
      const res = await axios.get(`${API}/reports/search-payments?${params.toString()}`);
      setDetailResults(res.data || []);
      if ((res.data || []).length === 0) {
        toast('No se encontraron resultados', { icon: 'ℹ️' });
      } else {
        toast.success(`${res.data.length} resultado(s) encontrado(s)`);
      }
    } catch (error) {
      toast.error('Error al buscar pagos');
    } finally {
      setDetailLoading(false);
    }
  };

  const computeStats = () => {
    let filtered = [...payments];
    
    // Apply filters
    if (filterStore !== 'all') {
      filtered = filtered.filter(p => p.tienda_id === filterStore);
    }
    if (filterProvider !== 'all') {
      // Buscar por ID o por nombre (para mayor flexibilidad)
      const selectedProvider = providers.find(prov => prov.id === filterProvider);
      if (selectedProvider) {
        filtered = filtered.filter(p => 
          p.proveedor_id === filterProvider || 
          p.proveedor_nombre?.toLowerCase().includes(selectedProvider.nombre?.toLowerCase())
        );
      } else {
        filtered = filtered.filter(p => p.proveedor_id === filterProvider);
      }
    }
    if (filterTipoPago !== 'all') {
      filtered = filtered.filter(p => p.tipo_pago === filterTipoPago);
    }
    if (startDate) {
      filtered = filtered.filter(p => new Date(p.created_at) >= new Date(startDate));
    }
    if (endDate) {
      filtered = filtered.filter(p => new Date(p.created_at) <= new Date(endDate + 'T23:59:59'));
    }

    // Calculate totals
    const pagados = filtered.filter(p => ['pagada', 'aprobada'].includes(p.status));
    const pendientes = filtered.filter(p => p.status === 'pendiente');
    const creditos = filtered.filter(p => p.tipo_pago === 'credito');
    const contados = filtered.filter(p => p.tipo_pago !== 'credito');

    const totalPagado = pagados.reduce((sum, p) => sum + (p.monto_depositar || 0), 0);
    const totalCredito = creditos.filter(p => ['pagada', 'aprobada'].includes(p.status)).reduce((sum, p) => sum + (p.monto_depositar || 0), 0);
    const totalContado = contados.filter(p => ['pagada', 'aprobada'].includes(p.status)).reduce((sum, p) => sum + (p.monto_depositar || 0), 0);
    const totalPendiente = pendientes.reduce((sum, p) => sum + (p.monto_depositar || 0), 0);

    // By Store
    const storeMap = {};
    pagados.forEach(p => {
      const storeName = p.tienda_nombre || p.centro_costo || 'Sin Tienda';
      if (!storeMap[storeName]) {
        storeMap[storeName] = { name: storeName, total: 0, credito: 0, contado: 0, count: 0 };
      }
      storeMap[storeName].total += p.monto_depositar || 0;
      storeMap[storeName].count += 1;
      if (p.tipo_pago === 'credito') {
        storeMap[storeName].credito += p.monto_depositar || 0;
      } else {
        storeMap[storeName].contado += p.monto_depositar || 0;
      }
    });
    const byStore = Object.values(storeMap).sort((a, b) => b.total - a.total);

    // By Provider (top 10)
    const providerMap = {};
    pagados.forEach(p => {
      const provName = p.proveedor_nombre || p.proveedor || 'Sin Proveedor';
      if (!providerMap[provName]) {
        providerMap[provName] = { name: provName.substring(0, 20), total: 0, count: 0 };
      }
      providerMap[provName].total += p.monto_depositar || 0;
      providerMap[provName].count += 1;
    });
    const byProvider = Object.values(providerMap).sort((a, b) => b.total - a.total).slice(0, 10);

    // By Month (last 6 months)
    const monthMap = {};
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = format(d, 'MMM yyyy', { locale: es });
      monthMap[key] = { name: key, credito: 0, contado: 0, total: 0 };
    }
    pagados.forEach(p => {
      const d = new Date(p.created_at);
      const key = format(d, 'MMM yyyy', { locale: es });
      if (monthMap[key]) {
        monthMap[key].total += p.monto_depositar || 0;
        if (p.tipo_pago === 'credito') {
          monthMap[key].credito += p.monto_depositar || 0;
        } else {
          monthMap[key].contado += p.monto_depositar || 0;
        }
      }
    });
    const byMonth = Object.values(monthMap);

    // By Week (last 7 days) - NEW
    const weekMap = {};
    for (let i = 6; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const key = format(d, 'EEE dd', { locale: es });
      weekMap[key] = { name: key, total: 0, count: 0 };
    }
    pagados.forEach(p => {
      const d = new Date(p.created_at);
      const key = format(d, 'EEE dd', { locale: es });
      if (weekMap[key]) {
        weekMap[key].total += p.monto_depositar || 0;
        weekMap[key].count += 1;
      }
    });
    const byWeek = Object.values(weekMap);

    // By Payment Method (Transferencia, Efectivo, Cheque) - NEW
    const methodMap = {
      transferencia: { name: 'Transferencia', total: 0, count: 0 },
      efectivo: { name: 'Efectivo', total: 0, count: 0 },
      cheque: { name: 'Cheque', total: 0, count: 0 }
    };
    pagados.forEach(p => {
      const method = p.forma_pago || 'transferencia';
      if (methodMap[method]) {
        methodMap[method].total += p.monto_depositar || 0;
        methodMap[method].count += 1;
      }
    });
    const byMethod = Object.values(methodMap).filter(m => m.total > 0);

    // Top 5 Stores Monthly Trend - NEW
    const topStoreNames = byStore.slice(0, 5).map(s => s.name);
    const storeMonthlyMap = {};
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthKey = format(d, 'MMM', { locale: es });
      storeMonthlyMap[monthKey] = { name: monthKey };
      topStoreNames.forEach(storeName => {
        storeMonthlyMap[monthKey][storeName] = 0;
      });
    }
    pagados.forEach(p => {
      const storeName = p.tienda_nombre || p.centro_costo || 'Sin Tienda';
      if (topStoreNames.includes(storeName)) {
        const d = new Date(p.created_at);
        const monthKey = format(d, 'MMM', { locale: es });
        if (storeMonthlyMap[monthKey]) {
          storeMonthlyMap[monthKey][storeName] += p.monto_depositar || 0;
        }
      }
    });
    const byStoreMonthly = Object.values(storeMonthlyMap);

    // Credits analysis
    const creditosVencidos = creditsAlert.filter(c => c.days_remaining <= 0).length;
    const creditosPorVencer = creditsAlert.filter(c => c.days_remaining > 0 && c.days_remaining <= 3).length;

    setStats({
      totalPagado,
      totalCredito,
      totalContado,
      totalPendiente,
      countPagados: pagados.length,
      countCredito: creditos.length,
      countContado: contados.length,
      countPendientes: pendientes.length,
      byStore,
      byProvider,
      byMonth,
      byWeek,
      byMethod,
      byStoreMonthly,
      topStoreNames,
      creditosVencidos,
      creditosPorVencer
    });
  };

  const clearFilters = () => {
    setFilterStore('all');
    setFilterProvider('all');
    setFilterTipoPago('all');
    setStartDate('');
    setEndDate('');
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    
    // Header - Blue banner
    doc.setFillColor(30, 58, 138); // Dark blue
    doc.rect(0, 0, 210, 35, 'F');
    
    // Company name
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text('TIENDAS DE CONVENIENCIA SIEMPRE', 105, 18, { align: 'center' });
    
    // Report subtitle
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text('Reporte de Pagos y Facturación', 105, 28, { align: 'center' });
    
    // Date
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.text(`Generado: ${format(new Date(), 'dd/MM/yyyy HH:mm', { locale: es })}`, 14, 45);
    
    // Filters applied
    let filterText = 'Filtros: ';
    if (filterStore !== 'all') filterText += `Tienda: ${stores.find(s => s.id === filterStore)?.nombre || filterStore}, `;
    if (filterProvider !== 'all') filterText += `Proveedor: ${providers.find(p => p.id === filterProvider)?.nombre || filterProvider}, `;
    if (filterTipoPago !== 'all') filterText += `Tipo: ${filterTipoPago}, `;
    if (startDate) filterText += `Desde: ${startDate}, `;
    if (endDate) filterText += `Hasta: ${endDate}`;
    if (filterText === 'Filtros: ') filterText = 'Filtros: Ninguno (todos los datos)';
    doc.text(filterText, 14, 52);
    
    // Summary stats
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Resumen General', 14, 65);
    
    autoTable(doc, {
      startY: 70,
      head: [['Métrica', 'Cantidad', 'Monto (Q)']],
      body: [
        ['Total Pagado', stats.countPagados, `Q${stats.totalPagado.toLocaleString('es-GT', { minimumFractionDigits: 2 })}`],
        ['Pagos a Crédito', stats.countCredito, `Q${stats.totalCredito.toLocaleString('es-GT', { minimumFractionDigits: 2 })}`],
        ['Pagos al Contado', stats.countContado, `Q${stats.totalContado.toLocaleString('es-GT', { minimumFractionDigits: 2 })}`],
        ['Pendientes', stats.countPendientes, `Q${stats.totalPendiente.toLocaleString('es-GT', { minimumFractionDigits: 2 })}`],
      ],
      theme: 'grid',
      headStyles: { fillColor: [30, 58, 138] },
    });
    
    // By Store table
    let lastY = doc.lastAutoTable?.finalY || 120;
    
    if (stats.byStore.length > 0) {
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Pagos por Sucursal', 14, lastY + 15);
      
      autoTable(doc, {
        startY: lastY + 20,
        head: [['Sucursal', 'Cantidad', 'Crédito (Q)', 'Contado (Q)', 'Total (Q)']],
        body: stats.byStore.map(s => [
          s.name,
          s.count,
          `Q${s.credito.toLocaleString('es-GT', { minimumFractionDigits: 2 })}`,
          `Q${s.contado.toLocaleString('es-GT', { minimumFractionDigits: 2 })}`,
          `Q${s.total.toLocaleString('es-GT', { minimumFractionDigits: 2 })}`
        ]),
        theme: 'grid',
        headStyles: { fillColor: [30, 58, 138] },
      });
    }
    
    // Credits alert
    if (creditsAlert.length > 0) {
      doc.addPage();
      
      // Header on new page
      doc.setFillColor(30, 58, 138);
      doc.rect(0, 0, 210, 20, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(14);
      doc.text('TIENDAS DE CONVENIENCIA SIEMPRE - Facturas a Crédito', 105, 13, { align: 'center' });
      
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Alertas de Crédito', 14, 35);
      
      autoTable(doc, {
        startY: 40,
        head: [['Proveedor', 'Monto (Q)', 'Fecha Vencimiento', 'Estado']],
        body: creditsAlert.map(c => [
          c.proveedor_nombre || c.proveedor,
          `Q${(c.monto_depositar || 0).toLocaleString('es-GT', { minimumFractionDigits: 2 })}`,
          c.fecha_pago_programada ? format(new Date(c.fecha_pago_programada), 'dd/MM/yyyy', { locale: es }) : 'N/A',
          c.days_remaining <= 0 ? `VENCIDO (${Math.abs(c.days_remaining)} días)` : `${c.days_remaining} días restantes`
        ]),
        theme: 'grid',
        headStyles: { fillColor: [30, 58, 138] },
        bodyStyles: { fontSize: 9 },
        didParseCell: (data) => {
          if (data.column.index === 3 && data.cell.raw.includes('VENCIDO')) {
            data.cell.styles.textColor = [220, 38, 38];
            data.cell.styles.fontStyle = 'bold';
          }
        }
      });
    }
    
    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text(`Página ${i} de ${pageCount}`, 105, 290, { align: 'center' });
    }
    
    doc.save(`reporte_siempre_${format(new Date(), 'yyyy-MM-dd')}.pdf`);
    toast.success('PDF generado exitosamente');
  };

  const exportToExcel = async () => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (filterStore !== 'all') params.append('tienda_id', filterStore);
      
      const response = await axios.get(`${API}/reports/export-excel?${params.toString()}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `pagos_siempre_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Excel exportado exitosamente');
    } catch (error) {
      toast.error('Error al exportar Excel');
    }
  };

  const exportCreditsPending = async () => {
    try {
      const response = await axios.get(`${API}/reports/export-credits-pending`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `creditos_pendientes_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Créditos pendientes exportados');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al exportar créditos');
    }
  };

  const exportCombustible = async () => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      const response = await axios.get(`${API}/reports/export-combustible?${params.toString()}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `reporte_combustible_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Reporte de combustible exportado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al exportar reporte de combustible');
    }
  };

  const formatCurrency = (value) => {
    return `Q${(value || 0).toLocaleString('es-GT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Pie chart data for credit vs contado
  const pieData = [
    { name: 'Crédito', value: stats.totalCredito, color: CREDIT_COLORS.credito },
    { name: 'Contado', value: stats.totalContado, color: CREDIT_COLORS.contado }
  ].filter(d => d.value > 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Cargando reportes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <div className="bg-blue-900 text-white py-6 px-4 shadow-lg">
        <div className="container mx-auto">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-white hover:bg-blue-800 mb-2">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Volver
              </Button>
              <h1 className="text-2xl md:text-3xl font-bold">Reportes y Estadísticas</h1>
              <p className="text-blue-200 text-sm mt-1">TIENDAS DE CONVENIENCIA SIEMPRE</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => setShowFilters(!showFilters)} variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20">
                <Filter className="mr-2 h-4 w-4" />
                Filtros
              </Button>
              <Button onClick={exportToPDF} className="bg-red-600 hover:bg-red-700 text-white">
                <FileText className="mr-2 h-4 w-4" />
                PDF
              </Button>
              <Button onClick={exportToExcel} className="bg-green-600 hover:bg-green-700 text-white">
                <Download className="mr-2 h-4 w-4" />
                Excel
              </Button>
              <Button onClick={exportCreditsPending} variant="outline" className="border-purple-500 text-purple-600 hover:bg-purple-50">
                <Download className="mr-2 h-4 w-4" />
                Créditos Pend.
              </Button>
              <Button onClick={exportCombustible} variant="outline" className="border-orange-500 text-orange-600 hover:bg-orange-50">
                <Download className="mr-2 h-4 w-4" />
                Combustible
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        {/* Filters Panel */}
        {showFilters && (
          <Card className="p-4 mb-6 bg-white shadow-md border-blue-200">
            <h3 className="font-semibold text-blue-900 mb-4 flex items-center">
              <Filter className="mr-2 h-5 w-5" />
              Filtros de Reporte
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div>
                <Label className="text-sm">Sucursal</Label>
                <Select value={filterStore} onValueChange={setFilterStore}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Todas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas las sucursales</SelectItem>
                    {stores.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm">Proveedor</Label>
                <Select value={filterProvider} onValueChange={setFilterProvider}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los proveedores</SelectItem>
                    {providers.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm">Tipo de Pago</Label>
                <Select value={filterTipoPago} onValueChange={setFilterTipoPago}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="credito">Crédito</SelectItem>
                    <SelectItem value="contado">Contado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm">Desde</Label>
                <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label className="text-sm">Hasta</Label>
                <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="mt-1" />
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <Button variant="outline" onClick={clearFilters}>Limpiar Filtros</Button>
            </div>
          </Card>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="p-4 bg-gradient-to-br from-emerald-500 to-emerald-600 text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-100 text-sm">Total Pagado</p>
                <p className="text-2xl md:text-3xl font-bold">{formatCurrency(stats.totalPagado)}</p>
                <p className="text-emerald-200 text-xs mt-1">{stats.countPagados} pagos</p>
              </div>
              <CheckCircle className="h-10 w-10 text-emerald-200" />
            </div>
          </Card>
          
          <Card className="p-4 bg-gradient-to-br from-amber-500 to-amber-600 text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-amber-100 text-sm">A Crédito</p>
                <p className="text-2xl md:text-3xl font-bold">{formatCurrency(stats.totalCredito)}</p>
                <p className="text-amber-200 text-xs mt-1">{stats.countCredito} facturas</p>
              </div>
              <CreditCard className="h-10 w-10 text-amber-200" />
            </div>
          </Card>
          
          <Card className="p-4 bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">Al Contado</p>
                <p className="text-2xl md:text-3xl font-bold">{formatCurrency(stats.totalContado)}</p>
                <p className="text-blue-200 text-xs mt-1">{stats.countContado} pagos</p>
              </div>
              <DollarSign className="h-10 w-10 text-blue-200" />
            </div>
          </Card>
          
          <Card className="p-4 bg-gradient-to-br from-slate-500 to-slate-600 text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-100 text-sm">Pendientes</p>
                <p className="text-2xl md:text-3xl font-bold">{formatCurrency(stats.totalPendiente)}</p>
                <p className="text-slate-200 text-xs mt-1">{stats.countPendientes} por pagar</p>
              </div>
              <Clock className="h-10 w-10 text-slate-200" />
            </div>
          </Card>
        </div>

        {/* Charts Row 1 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Monthly Trend */}
          <Card className="p-6 shadow-lg">
            <h3 className="font-semibold text-slate-800 mb-4 flex items-center">
              <TrendingUp className="mr-2 h-5 w-5 text-blue-600" />
              Tendencia Mensual (Últimos 6 meses)
            </h3>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={stats.byMonth}>
                <defs>
                  <linearGradient id="colorCredito" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.1}/>
                  </linearGradient>
                  <linearGradient id="colorContado" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0.1}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `Q${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
                <Area type="monotone" dataKey="credito" name="Crédito" stroke="#f59e0b" fill="url(#colorCredito)" />
                <Area type="monotone" dataKey="contado" name="Contado" stroke="#10b981" fill="url(#colorContado)" />
              </AreaChart>
            </ResponsiveContainer>
          </Card>

          {/* Pie Chart - Credit vs Contado */}
          <Card className="p-6 shadow-lg">
            <h3 className="font-semibold text-slate-800 mb-4 flex items-center">
              <CreditCard className="mr-2 h-5 w-5 text-amber-600" />
              Distribución: Crédito vs Contado
            </h3>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  innerRadius={60}
                  dataKey="value"
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex justify-center gap-6 mt-4">
              <div className="text-center">
                <div className="w-4 h-4 bg-amber-500 rounded mx-auto mb-1"></div>
                <p className="text-sm text-slate-600">Crédito</p>
                <p className="font-bold text-amber-600">{formatCurrency(stats.totalCredito)}</p>
              </div>
              <div className="text-center">
                <div className="w-4 h-4 bg-emerald-500 rounded mx-auto mb-1"></div>
                <p className="text-sm text-slate-600">Contado</p>
                <p className="font-bold text-[#2c57c5]">{formatCurrency(stats.totalContado)}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Charts Row 2 - NEW */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Weekly Trend */}
          <Card className="p-6 shadow-lg">
            <h3 className="font-semibold text-slate-800 mb-4 flex items-center">
              <Calendar className="mr-2 h-5 w-5 text-purple-600" />
              Tendencia Semanal (Últimos 7 días)
            </h3>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={stats.byWeek}>
                <defs>
                  <linearGradient id="colorWeekly" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.1}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `Q${(v/1000).toFixed(0)}k`} />
                <Tooltip 
                  formatter={(value, name) => [formatCurrency(value), 'Total']}
                  labelFormatter={(label) => `Día: ${label}`}
                />
                <Area type="monotone" dataKey="total" name="Total Pagado" stroke="#8b5cf6" fill="url(#colorWeekly)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
            <div className="flex justify-center gap-6 mt-4">
              <div className="text-center">
                <p className="text-sm text-slate-600">Promedio Diario</p>
                <p className="font-bold text-purple-600">
                  {formatCurrency(stats.byWeek.reduce((sum, d) => sum + d.total, 0) / 7)}
                </p>
              </div>
              <div className="text-center">
                <p className="text-sm text-slate-600">Total 7 días</p>
                <p className="font-bold text-purple-600">
                  {formatCurrency(stats.byWeek.reduce((sum, d) => sum + d.total, 0))}
                </p>
              </div>
            </div>
          </Card>

          {/* Payment Method Distribution */}
          <Card className="p-6 shadow-lg">
            <h3 className="font-semibold text-slate-800 mb-4 flex items-center">
              <DollarSign className="mr-2 h-5 w-5 text-cyan-600" />
              Distribución por Forma de Pago
            </h3>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={stats.byMethod}
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  innerRadius={50}
                  dataKey="total"
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                >
                  <Cell fill="#6366f1" /> {/* Transferencia - Indigo */}
                  <Cell fill="#06b6d4" /> {/* Efectivo - Cyan */}
                  <Cell fill="#ec4899" /> {/* Cheque - Pink */}
                </Pie>
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex justify-center gap-4 mt-4 flex-wrap">
              {stats.byMethod.map((method, idx) => (
                <div key={method.name} className="text-center">
                  <div className={`w-4 h-4 rounded mx-auto mb-1 ${idx === 0 ? 'bg-indigo-500' : idx === 1 ? 'bg-cyan-500' : 'bg-pink-500'}`}></div>
                  <p className="text-xs text-slate-600">{method.name}</p>
                  <p className="font-semibold text-sm">{formatCurrency(method.total)}</p>
                  <p className="text-xs text-slate-400">{method.count} pagos</p>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Charts Row 3 - Top 5 Stores Monthly Trend - NEW */}
        {stats.topStoreNames && stats.topStoreNames.length > 0 && (
          <Card className="p-6 shadow-lg mb-6">
            <h3 className="font-semibold text-slate-800 mb-4 flex items-center">
              <TrendingUp className="mr-2 h-5 w-5 text-[#2c57c5]" />
              Top 5 Sucursales - Evolución Mensual
            </h3>
            <ResponsiveContainer width="100%" height={350}>
              <AreaChart data={stats.byStoreMonthly}>
                <defs>
                  {stats.topStoreNames.map((name, idx) => (
                    <linearGradient key={name} id={`colorStore${idx}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={COLORS[idx]} stopOpacity={0.6}/>
                      <stop offset="95%" stopColor={COLORS[idx]} stopOpacity={0.05}/>
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `Q${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
                {stats.topStoreNames.map((name, idx) => (
                  <Area 
                    key={name}
                    type="monotone" 
                    dataKey={name} 
                    name={name.substring(0, 20)} 
                    stroke={COLORS[idx]} 
                    fill={`url(#colorStore${idx})`}
                    strokeWidth={2}
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          </Card>
        )}

        {/* Stores Chart */}
        <Card className="p-6 shadow-lg mb-6">
          <h3 className="font-semibold text-slate-800 mb-4 flex items-center">
            <Store className="mr-2 h-5 w-5 text-purple-600" />
            Pagos por Sucursal
          </h3>
          {stats.byStore.length > 0 ? (
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={stats.byStore} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis type="number" tickFormatter={(v) => `Q${(v/1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
                <Bar dataKey="credito" name="Crédito" fill="#f59e0b" stackId="a" />
                <Bar dataKey="contado" name="Contado" fill="#10b981" stackId="a" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-center text-slate-500 py-10">No hay datos de sucursales para mostrar</p>
          )}
        </Card>

        {/* Credits Alert Section */}
        <Card className="p-6 shadow-lg mb-6 border-l-4 border-amber-500">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-800 flex items-center">
              <AlertTriangle className="mr-2 h-5 w-5 text-amber-600" />
              Facturas a Crédito - Alertas
            </h3>
            <div className="flex gap-2">
              {stats.creditosVencidos > 0 && (
                <Badge className="bg-red-600 text-white px-3 py-1">
                  {stats.creditosVencidos} Vencidas
                </Badge>
              )}
              {stats.creditosPorVencer > 0 && (
                <Badge className="bg-amber-500 text-white px-3 py-1">
                  {stats.creditosPorVencer} Por vencer (3 días)
                </Badge>
              )}
            </div>
          </div>
          
          {creditsAlert.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-100">
                    <th className="text-left p-3 font-semibold">Proveedor</th>
                    <th className="text-left p-3 font-semibold">Sucursal</th>
                    <th className="text-right p-3 font-semibold">Monto</th>
                    <th className="text-center p-3 font-semibold">Vencimiento</th>
                    <th className="text-center p-3 font-semibold">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {creditsAlert.map((credit, idx) => (
                    <tr 
                      key={credit.id} 
                      className={`border-b cursor-pointer hover:bg-slate-50 ${credit.days_remaining <= 0 ? 'bg-red-50' : credit.days_remaining <= 3 ? 'bg-amber-50' : ''}`}
                      onClick={() => navigate(`/payments/${credit.id}`)}
                    >
                      <td className="p-3 font-medium">{credit.proveedor_nombre || credit.proveedor}</td>
                      <td className="p-3 text-slate-600">{credit.tienda_nombre || credit.centro_costo || '-'}</td>
                      <td className="p-3 text-right font-semibold">{formatCurrency(credit.monto_depositar)}</td>
                      <td className="p-3 text-center">
                        {credit.fecha_pago_programada ? format(new Date(credit.fecha_pago_programada), 'dd/MM/yyyy', { locale: es }) : '-'}
                      </td>
                      <td className="p-3 text-center">
                        {credit.days_remaining <= 0 ? (
                          <Badge className="bg-red-600 text-white">
                            VENCIDO ({Math.abs(credit.days_remaining)}d)
                          </Badge>
                        ) : credit.days_remaining <= 3 ? (
                          <Badge className="bg-amber-500 text-white">
                            {credit.days_remaining} días
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-slate-600">
                            {credit.days_remaining} días
                          </Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-10 text-slate-500">
              <CheckCircle className="h-12 w-12 mx-auto text-emerald-500 mb-2" />
              <p>No hay facturas a crédito pendientes</p>
            </div>
          )}
        </Card>

        {/* Provider Detail Search - Server-side */}
        <Card className="p-6 shadow-lg mb-6 border-l-4 border-indigo-500">
          <h3 className="font-semibold text-slate-800 mb-4 flex items-center">
            <Users className="mr-2 h-5 w-5 text-[#2c57c5]" />
            Detalle por Proveedor y Sucursal
          </h3>
          
          {/* Search filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
            <div>
              <Label className="text-sm">Buscar Proveedor</Label>
              <Input
                placeholder="Escriba nombre del proveedor..."
                value={providerSearch}
                onChange={(e) => setProviderSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && searchDetail()}
                className="mt-1"
                data-testid="detail-provider-search"
              />
            </div>
            <div>
              <Label className="text-sm">Sucursal</Label>
              <Select value={storeSearch} onValueChange={setStoreSearch}>
                <SelectTrigger className="mt-1" data-testid="detail-store-filter">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las sucursales</SelectItem>
                  {stores.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">Estado</Label>
              <Select value={detailStatusFilter} onValueChange={setDetailStatusFilter}>
                <SelectTrigger className="mt-1" data-testid="detail-status-filter">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los estados</SelectItem>
                  <SelectItem value="pagada">Pagadas</SelectItem>
                  <SelectItem value="pendiente">Pendientes</SelectItem>
                  <SelectItem value="rechazada">Rechazadas</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div>
              <Label className="text-sm">Fecha Desde</Label>
              <Input type="date" value={detailStartDate} onChange={(e) => setDetailStartDate(e.target.value)} className="mt-1" data-testid="detail-start-date" />
            </div>
            <div>
              <Label className="text-sm">Fecha Hasta</Label>
              <Input type="date" value={detailEndDate} onChange={(e) => setDetailEndDate(e.target.value)} className="mt-1" data-testid="detail-end-date" />
            </div>
            <div className="flex items-end">
              <Button 
                onClick={searchDetail}
                className="w-full bg-[#2c57c5] hover:bg-[#1e3a8a] text-white"
                disabled={detailLoading}
                data-testid="detail-search-btn"
              >
                {detailLoading ? 'Buscando...' : 'Buscar'}
              </Button>
            </div>
            <div className="flex items-end">
              <Button 
                variant="outline" 
                onClick={() => { setProviderSearch(''); setStoreSearch('all'); setDetailStatusFilter('all'); setDetailStartDate(''); setDetailEndDate(''); setDetailResults([]); setDetailSearched(false); }}
                className="w-full"
                data-testid="detail-clear-btn"
              >
                Limpiar
              </Button>
            </div>
          </div>
          
          {/* Results table */}
          {detailSearched && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-indigo-50">
                  <th className="text-left p-3 font-semibold">Proveedor</th>
                  <th className="text-left p-3 font-semibold">Sucursal</th>
                  <th className="text-left p-3 font-semibold">Reportado por</th>
                  <th className="text-center p-3 font-semibold">Tipo</th>
                  <th className="text-center p-3 font-semibold">Forma Pago</th>
                  <th className="text-right p-3 font-semibold">Monto</th>
                  <th className="text-center p-3 font-semibold">Estado</th>
                  <th className="text-center p-3 font-semibold">Fecha</th>
                </tr>
              </thead>
              <tbody>
                {detailResults.map((payment, idx) => (
                    <tr 
                      key={payment.id} 
                      className={`border-b hover:bg-slate-50 cursor-pointer ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}
                      onClick={() => navigate(`/payments/${payment.id}`)}
                      data-testid={`detail-row-${idx}`}
                    >
                      <td className="p-3 font-medium">{payment.proveedor_nombre || payment.proveedor}</td>
                      <td className="p-3 text-slate-600">{payment.tienda_nombre || payment.centro_costo || '-'}</td>
                      <td className="p-3 text-slate-600 text-xs">{payment.nombre_envia || '-'}</td>
                      <td className="p-3 text-center">
                        {payment.tipo_pago === 'credito' ? (
                          <Badge className="bg-amber-500 text-white">CREDITO</Badge>
                        ) : (
                          <Badge className="bg-emerald-500 text-white">CONTADO</Badge>
                        )}
                      </td>
                      <td className="p-3 text-center">
                        {payment.forma_pago === 'efectivo' ? (
                          <Badge variant="outline" className="text-blue-600 border-blue-300">Efectivo</Badge>
                        ) : payment.forma_pago === 'cheque' ? (
                          <Badge variant="outline" className="text-pink-600 border-pink-300">Cheque</Badge>
                        ) : (
                          <Badge variant="outline" className="text-[#2c57c5] border-indigo-300">Transferencia</Badge>
                        )}
                      </td>
                      <td className="p-3 text-right font-semibold">{formatCurrency(payment.monto_depositar)}</td>
                      <td className="p-3 text-center">
                        {payment.status === 'pagada' || payment.status === 'aprobada' ? (
                          <Badge className="bg-emerald-100 text-[#1e3a8a]">Pagado</Badge>
                        ) : payment.status === 'rechazada' ? (
                          <Badge className="bg-red-100 text-red-700">Rechazado</Badge>
                        ) : (
                          <Badge className="bg-slate-100 text-slate-700">Pendiente</Badge>
                        )}
                      </td>
                      <td className="p-3 text-center text-slate-600">
                        {format(new Date(payment.created_at), 'dd/MM/yy', { locale: es })}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
            {detailResults.length === 0 && (
              <div className="text-center py-10 text-slate-500">
                No se encontraron pagos con los filtros seleccionados
              </div>
            )}
          </div>
          )}
          
          {/* Summary for search results */}
          {detailSearched && detailResults.length > 0 && (
            <div className="mt-4 p-4 bg-indigo-50 rounded-lg">
              <h4 className="font-semibold text-indigo-900 mb-2">Resumen de busqueda ({detailResults.length} facturas):</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                {(() => {
                  const totalFiltered = detailResults.reduce((sum, p) => sum + (p.monto_depositar || 0), 0);
                  const creditoFiltered = detailResults.filter(p => p.tipo_pago === 'credito').reduce((sum, p) => sum + (p.monto_depositar || 0), 0);
                  const contadoFiltered = detailResults.filter(p => p.tipo_pago !== 'credito').reduce((sum, p) => sum + (p.monto_depositar || 0), 0);
                  const pagadosFiltered = detailResults.filter(p => ['pagada', 'aprobada'].includes(p.status)).length;
                  
                  return (
                    <>
                      <div>
                        <p className="text-slate-600">Total Facturas</p>
                        <p className="font-bold text-[#1e3a8a]">{detailResults.length}</p>
                      </div>
                      <div>
                        <p className="text-slate-600">Monto Total</p>
                        <p className="font-bold text-[#1e3a8a]">{formatCurrency(totalFiltered)}</p>
                      </div>
                      <div>
                        <p className="text-slate-600">A Credito</p>
                        <p className="font-bold text-amber-600">{formatCurrency(creditoFiltered)}</p>
                      </div>
                      <div>
                        <p className="text-slate-600">Al Contado</p>
                        <p className="font-bold text-[#2c57c5]">{formatCurrency(contadoFiltered)}</p>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          )}
        </Card>

        {/* Top Providers */}
        <Card className="p-6 shadow-lg">
          <h3 className="font-semibold text-slate-800 mb-4 flex items-center">
            <Users className="mr-2 h-5 w-5 text-[#2c57c5]" />
            Top 10 Proveedores por Monto
          </h3>
          {stats.byProvider.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={stats.byProvider}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="name" tick={{ fontSize: 10, angle: -45, textAnchor: 'end' }} height={80} />
                <YAxis tickFormatter={(v) => `Q${(v/1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Bar dataKey="total" name="Total Pagado" fill="#6366f1" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-center text-slate-500 py-10">No hay datos de proveedores para mostrar</p>
          )}
        </Card>
      </div>
    </div>
  );
}
